import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import {
  DataAccessEventsFacade,
  DataAccessExhibitorsFacade,
  DataAccessPostsFacade,
  DataAccessProductsFacade,
} from '@planone/bau365/data-access-fair-data';
import { ContactPerson, Contributor, Event, Exhibitor, Post, Product } from '@planone/bau365/util-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import { GoogleAnalyticsService } from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { SnowplowService } from '@planone/shared/feature-tracking-snowplow';
import { localizedMeta, MetaService } from '@planone/shared/util-meta';
import { combineLatestWith, filter, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';

import * as LinkClickActions from './link-click.actions';
import {
  EventLinkClickEventInterface,
  ExhibitorLinkClickEventInterface,
  PostLinkClickEventInterface,
  ProductLinkClickEventInterface,
} from './link-click.models';

import { DataTransformationHelper } from '../../helper/data-transformation.helpers';
import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class LinkClickEffects {
  generalLinkClick$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LinkClickActions.linkClick),
        filter(
          (action: any) =>
            action.entityType === undefined || action.entityType === 'GENERAL' || action.entityType === 'UNKNOWN'
        ),
        withLatestFrom(this.authenticationFacade.excludeAnalytics$),
        tap(([action, exclude_analytics]) => {
          if (!this.snowplowService.isExcludedFromClickTracking(action.htmlElement?.url)) {
            this.store.dispatch(
              FeatureTrackingSnowplow.linkClick({
                targetUrl: action.htmlElement?.url,
                elementId: action.htmlElement?.id,
                elementClasses: action.htmlElement?.classes,
                elementTarget: action.htmlElement?.target,
                elementContent: action.linkText ?? action.htmlElement.innerHTML,
                context: [
                  {
                    schema: CustomEventIglu.link_click_additions,
                    data: {
                      link_type: action.linkType,
                      link_text: action.linkText ?? action.htmlElement.innerHTML,
                      page_type: action.pageType,
                      context: action.context,
                      exclude_analytics: exclude_analytics,
                    },
                  },
                ],
              })
            );
          }

          if (!this.googleAnalyticsService.isExcludedFromClickTracking(action.htmlElement?.url)) {
            this.store.dispatch(
              FeatureTrackingGoogleAnalytics.push({
                event: 'click',
                context: action.context,
                link_type: action.linkType,
                link_text: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                link_classes: action.htmlElement?.classes,
                link_id: action.htmlElement?.id,
                link_url: action.htmlElement?.url,
                outbound: true,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
              })
            );
          }
        })
      ),
    { dispatch: false }
  );

  eventLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'EVENT'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event),
          take(1),
          map((event) => [action, event])
        );
      }),
      switchMap(([action, event]) =>
        this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[EventLinkClickEventInterface, Event, Exhibitor, boolean]>[action, event, exhibitor, exclude_analytics]
          ),
          switchMap(([action, event, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', event);

            const snowplowContext = [
              {
                schema: CustomEventIglu.link_click_additions,
                data: {
                  link_type: action.linkType,
                  link_text: this.dataTransformationHelper.snowplow?.filter(
                    action.linkType,
                    action.linkText ?? action.htmlElement?.content
                  ),
                  page_type: action.pageType,
                  context: action.context,
                  exclude_analytics: exclude_analytics,
                },
              },
            ];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowEventContext) snowplowContext.push(snowplowEventContext);

            return of(
              FeatureTrackingSnowplow.linkClick({
                targetUrl:
                  this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                elementId: action.htmlElement?.id,
                elementClasses: action.htmlElement?.classes,
                elementTarget: action.htmlElement?.target,
                elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'click',
                context: action.context,
                link_type: action.linkType,
                link_text: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                link_classes: action.htmlElement?.classes,
                link_id: action.htmlElement?.id,
                link_url:
                  this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                outbound: true,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
              })
            );
          })
        )
      )
    )
  );

  contributorLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'CONTRIBUTOR'),
      switchMap((action) => {
        return this.eventsFacade.event$.pipe(
          filter((event) => !!event),
          take(1),
          map((event) => [action, event])
        );
      }),
      switchMap(([action, event]) =>
        this.exhibitorsFacade.getExhibitorById(event?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          switchMap((exhibitor) =>
            this.eventsFacade.contributor$.pipe(
              filter((contributor) => !!contributor),
              take(1),
              withLatestFrom(this.authenticationFacade.excludeAnalytics$),
              map(
                ([contributor, exclude_analytics]) =>
                  <[EventLinkClickEventInterface, Event, Contributor, Exhibitor, boolean]>[
                    action,
                    event,
                    contributor,
                    exhibitor,
                    exclude_analytics,
                  ]
              ),
              switchMap(([action, event, contributor, exhibitor, exclude_analytics]) => {
                const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
                const snowplowContributorContext = this.dataTransformationHelper.snowplow?.map(
                  'CONTRIBUTOR',
                  contributor,
                  exhibitor.fair_id
                );
                const snowplowEventContext = this.dataTransformationHelper.snowplow?.map('EVENT', event);

                const snowplowContext = [
                  {
                    schema: CustomEventIglu.link_click_additions,
                    data: {
                      link_type: action.linkType,
                      link_text: this.dataTransformationHelper.snowplow?.filter(
                        action.linkType,
                        action.linkText ?? action.htmlElement?.content
                      ),
                      page_type: action.pageType,
                      context: action.context,
                      exclude_analytics: exclude_analytics,
                    },
                  },
                ];

                if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
                if (snowplowEventContext) snowplowContext.push(snowplowEventContext);
                if (snowplowContributorContext) snowplowContext.push(snowplowContributorContext);

                const title = this.metaService.getTitle(localizedMeta['titleExhibitorContactPersonDetails'], [
                  'Anonymized',
                ]);

                return of(
                  FeatureTrackingSnowplow.linkClick({
                    targetUrl:
                      this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
                      'anonymized-url',
                    elementId: action.htmlElement?.id,
                    elementClasses: action.htmlElement?.classes,
                    elementTarget: action.htmlElement?.target,
                    elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
                      action.linkType,
                      action.linkText ?? action.htmlElement?.content
                    ),
                    context: snowplowContext ? snowplowContext : undefined,
                  }),
                  FeatureTrackingGoogleAnalytics.push({
                    event: 'click',
                    context: action.context,
                    link_type: action.linkType,
                    link_text: this.dataTransformationHelper.googleAnalytics?.filter(
                      action.linkType,
                      action.linkText ?? action.htmlElement?.content
                    ),
                    link_classes: action.htmlElement?.classes,
                    link_id: action.htmlElement?.id,
                    link_url:
                      this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
                      'anonymized-url',
                    outbound: true,
                    page_title: title,
                    page_type: action.pageType,
                    exclude_analytics: exclude_analytics,
                    ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                    ...this.dataTransformationHelper.googleAnalytics?.map('EVENT', event),
                    ...this.dataTransformationHelper.googleAnalytics?.map('CONTRIBUTOR', contributor),
                  })
                );
              })
            )
          )
        )
      )
    )
  );

  exhibitorLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'EXHIBITOR'),
      switchMap((action) => {
        return this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          map((exhibitor) => [action, exhibitor])
        );
      }),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      map(
        ([[action, exhibitor], exclude_analytics]) =>
          <[ExhibitorLinkClickEventInterface, Exhibitor, boolean]>[action, exhibitor, exclude_analytics]
      ),
      switchMap(([action, exhibitor, exclude_analytics]) => {
        const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);

        const snowplowContext = [
          {
            schema: CustomEventIglu.link_click_additions,
            data: {
              link_type: action.linkType,
              link_text: this.dataTransformationHelper.snowplow?.filter(
                action.linkType,
                action.linkText ?? action.htmlElement?.content
              ),
              page_type: action.pageType,
              context: action.context,
              exclude_analytics: exclude_analytics,
            },
          },
        ];

        if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);

        if (action.contactPerson) {
          const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
            'CONTACT_PERSON',
            action.contactPerson,
            action.context
          );

          if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);
        }

        return of(
          FeatureTrackingSnowplow.linkClick({
            targetUrl:
              this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            elementId: action.htmlElement?.id,
            elementClasses: action.htmlElement?.classes,
            elementTarget: action.htmlElement?.target,
            elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            context: snowplowContext ? snowplowContext : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'click',
            context: action.context,
            link_type: action.linkType,
            link_text: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            link_classes: action.htmlElement?.classes,
            link_id: action.htmlElement?.id,
            link_url:
              this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            outbound: true,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
            ...(action.contactPerson &&
              this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', action.contactPerson)),
          })
        );
      })
    )
  );

  contactPersonLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'CONTACT_PERSON'),
      switchMap((action) => {
        return this.exhibitorsFacade.exhibitor$.pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          map((exhibitor) => [action, exhibitor])
        );
      }),
      combineLatestWith(
        this.exhibitorsFacade.exhibitorContactPerson$.pipe(
          filter((contactPerson) => !!contactPerson),
          take(1)
        )
      ),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      map(
        ([[[action, exhibitor], contactPerson], exclude_analytics]) =>
          <[ExhibitorLinkClickEventInterface, Exhibitor, ContactPerson, boolean]>[
            action,
            exhibitor,
            contactPerson,
            exclude_analytics,
          ]
      ),
      switchMap(([action, exhibitor, contactPerson, exclude_analytics]) => {
        const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
        const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
          'CONTACT_PERSON',
          contactPerson,
          action.context
        );

        const snowplowContext = [
          {
            schema: CustomEventIglu.link_click_additions,
            data: {
              link_type: action.linkType,
              link_text: this.dataTransformationHelper.snowplow?.filter(
                action.linkType,
                action.linkText ?? action.htmlElement?.content
              ),
              page_type: action.pageType,
              context: action.context,
              exclude_analytics: exclude_analytics,
            },
          },
        ];

        if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
        if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);

        const title = this.metaService.getTitle(localizedMeta['titleExhibitorContactPersonDetails'], ['Anonymized']);

        return of(
          FeatureTrackingSnowplow.linkClick({
            targetUrl:
              this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            elementId: action.htmlElement?.id,
            elementClasses: action.htmlElement?.classes,
            elementTarget: action.htmlElement?.target,
            elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            context: snowplowContext ? snowplowContext : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'click',
            context: action.context,
            link_type: action.linkType,
            link_text: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            link_classes: action.htmlElement?.classes,
            link_id: action.htmlElement?.id,
            link_url:
              this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            outbound: true,
            page_title: title,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
            ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
            ...this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', contactPerson),
          })
        );
      })
    )
  );

  postLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'POST'),
      switchMap((action) => {
        return this.postsFacade.post$.pipe(
          filter((post) => !!post),
          take(1),
          map((post) => [action, post])
        );
      }),
      switchMap(([action, post]) =>
        this.exhibitorsFacade.getExhibitorById(post?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[PostLinkClickEventInterface, Post, Exhibitor, boolean]>[action, post, exhibitor, exclude_analytics]
          ),
          switchMap(([action, post, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowPostContext = this.dataTransformationHelper.snowplow?.map('POST', post);

            const snowplowContext = [
              {
                schema: CustomEventIglu.link_click_additions,
                data: {
                  link_type: action.linkType,
                  link_text: this.dataTransformationHelper.snowplow?.filter(
                    action.linkType,
                    action.linkText ?? action.htmlElement?.content
                  ),
                  page_type: action.pageType,
                  context: action.context,
                  exclude_analytics: exclude_analytics,
                },
              },
            ];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowPostContext) snowplowContext.push(snowplowPostContext);

            if (action.contactPerson) {
              const snowplowContactPersonContext = this.dataTransformationHelper.snowplow?.map(
                'CONTACT_PERSON',
                action.contactPerson,
                action.context
              );

              if (snowplowContactPersonContext) snowplowContext.push(snowplowContactPersonContext);
            }

            return of(
              FeatureTrackingSnowplow.linkClick({
                targetUrl:
                  this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                elementId: action.htmlElement?.id,
                elementClasses: action.htmlElement?.classes,
                elementTarget: action.htmlElement?.target,
                elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'click',
                context: action.context,
                link_type: action.linkType,
                link_text: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                link_classes: action.htmlElement?.classes,
                link_id: action.htmlElement?.id,
                link_url:
                  this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                outbound: true,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('POST', post),
                ...(action.contactPerson &&
                  this.dataTransformationHelper.googleAnalytics?.map('CONTACT_PERSON', action.contactPerson)),
              })
            );
          })
        )
      )
    )
  );

  productLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'PRODUCT'),
      switchMap((action) => {
        return this.productsFacade.product$.pipe(
          filter((product) => !!product),
          take(1),
          map((product) => [action, product])
        );
      }),
      switchMap(([action, product]) =>
        this.exhibitorsFacade.getExhibitorById(product?.exhibitor_id).pipe(
          filter((exhibitor) => !!exhibitor),
          take(1),
          withLatestFrom(this.authenticationFacade.excludeAnalytics$),
          map(
            ([exhibitor, exclude_analytics]) =>
              <[ProductLinkClickEventInterface, Product, Exhibitor, boolean]>[
                action,
                product,
                exhibitor,
                exclude_analytics,
              ]
          ),
          switchMap(([action, product, exhibitor, exclude_analytics]) => {
            const snowplowExhibitorContext = this.dataTransformationHelper.snowplow?.map('EXHIBITOR', exhibitor);
            const snowplowProductContext = this.dataTransformationHelper.snowplow?.map(
              'PRODUCT',
              product,
              exhibitor.fair_id
            );

            const snowplowContext = [
              {
                schema: CustomEventIglu.link_click_additions,
                data: {
                  link_type: action.linkType,
                  link_text: this.dataTransformationHelper.snowplow?.filter(
                    action.linkType,
                    action.linkText ?? action.htmlElement?.content
                  ),
                  page_type: action.pageType,
                  context: action.context,
                  exclude_analytics: exclude_analytics,
                },
              },
            ];

            if (snowplowExhibitorContext) snowplowContext.push(snowplowExhibitorContext);
            if (snowplowProductContext) snowplowContext.push(snowplowProductContext);

            return of(
              FeatureTrackingSnowplow.linkClick({
                targetUrl:
                  this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                elementId: action.htmlElement?.id,
                elementClasses: action.htmlElement?.classes,
                elementTarget: action.htmlElement?.target,
                elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                context: snowplowContext ? snowplowContext : undefined,
              }),
              FeatureTrackingGoogleAnalytics.push({
                event: 'click',
                context: action.context,
                link_type: action.linkType,
                link_text: this.dataTransformationHelper.googleAnalytics?.filter(
                  action.linkType,
                  action.linkText ?? action.htmlElement?.content
                ),
                link_classes: action.htmlElement?.classes,
                link_id: action.htmlElement?.id,
                link_url:
                  this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
                  'anonymized-url',
                outbound: true,
                page_type: action.pageType,
                exclude_analytics: exclude_analytics,
                ...this.dataTransformationHelper.googleAnalytics?.map('EXHIBITOR', exhibitor),
                ...this.dataTransformationHelper.googleAnalytics?.map('PRODUCT', product),
              })
            );
          })
        )
      )
    )
  );

  hallplanPdfLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'HALLPLAN_PDF'),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) => {
        const snowplowContext = [
          {
            schema: CustomEventIglu.link_click_additions,
            data: {
              link_type: action.linkType,
              link_text: this.dataTransformationHelper.snowplow?.filter(
                action.linkType,
                action.linkText ?? action.htmlElement?.content
              ),
              page_type: action.pageType,
              context: action.context,
              hallname: action.hallname,
              exclude_analytics,
            },
          },
        ];
        return of(
          FeatureTrackingSnowplow.linkClick({
            targetUrl:
              this.dataTransformationHelper.snowplow?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            elementId: action.htmlElement?.id,
            elementClasses: action.htmlElement?.classes,
            elementTarget: action.htmlElement?.target,
            elementContent: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            context: snowplowContext ? snowplowContext : undefined,
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'click',
            context: action.context,
            link_type: action.linkType,
            link_text: this.dataTransformationHelper.googleAnalytics?.filter(
              action.linkType,
              action.linkText ?? action.htmlElement?.content
            ),
            link_classes: action.htmlElement?.classes,
            link_id: action.htmlElement?.id,
            link_url:
              this.dataTransformationHelper.googleAnalytics?.filter(action.linkType, action.htmlElement?.url) ??
              'anonymized-url',
            hallname: action.hallname,
            outbound: true,
            page_type: action.pageType,
            exclude_analytics,
          })
        );
      })
    )
  );

  quicklinkLinkClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkClickActions.linkClick),
      filter((action: any) => action.entityType === 'QUICKLINK'),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) =>
        of(
          FeatureTrackingSnowplow.linkClick({
            targetUrl: action.targetUrl,
            context: [
              {
                schema: CustomEventIglu.link_click_additions,
                data: {
                  link_type: action.linkType,
                  page_type: action.pageType,
                  context: action.context,
                  exclude_analytics,
                },
              },
            ],
          }),
          FeatureTrackingGoogleAnalytics.push({
            event: 'click',
            context: action.context,
            link_type: action.linkType,
            link_url: action.targetUrl,
            page_type: action.pageType,
            exclude_analytics,
            outbound: true,
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private metaService: MetaService,
    private authenticationFacade: AuthenticationFacade,
    private exhibitorsFacade: DataAccessExhibitorsFacade,
    private eventsFacade: DataAccessEventsFacade,
    private postsFacade: DataAccessPostsFacade,
    private productsFacade: DataAccessProductsFacade,
    private dataTransformationHelper: DataTransformationHelper,
    private googleAnalyticsService: GoogleAnalyticsService,
    private snowplowService: SnowplowService
  ) {}
}
